import type { AxiosInstance } from 'axios';

export type AnalyticsPeriod = 'DAY' | 'WEEK' | 'MONTH' | 'YEAR';
export type AnalyticsDomain = 'CUSTOMER' | 'VENDOR' | 'FINANCE' | 'OPS' | 'PLATFORM';

export interface PlatformMetrics {
  gmvPaise: number;
  orderCount: number;
  uniqueCustomerCount: number;
  posSalesPaise: number;
  b2bSalesPaise: number;
  marketplaceSalesPaise: number;
  saasRevenuePaise: number;
}

export interface FinanceMetrics {
  revenuePaise: number;
  cogsPaise: number;
  operatingExpensePaise: number;
  netPaise: number;
  grossMarginBps: number;
  netMarginBps: number;
  burnRatePaise: number;
}

export interface OpsMetrics {
  deliveredCount: number;
  onTimeCount: number;
  slaBps: number;
  settledGmvPaise: number;
  inventoryValuePaise: number;
  damagePaise: number;
}

export interface VendorMetrics {
  vendorId: string;
  gmvPaise: number;
  orderCount: number;
  deliveredCount: number;
  returnedCount: number;
  returnRateBps: number;
  fulfillmentRateBps: number;
  avgOrderValuePaise: number;
}

export interface CustomerMetrics {
  userId: string;
  orderCount: number;
  lifetimeValuePaise: number;
  avgOrderValuePaise: number;
  repeatOrders: number;
  daysSinceLastOrder: number | null;
}

export interface Dashboard {
  generatedAt: string;
  platform: PlatformMetrics;
  finance: FinanceMetrics;
  ops: OpsMetrics;
}

export interface AnalyticsSnapshot {
  id: string;
  domain: AnalyticsDomain;
  period: AnalyticsPeriod;
  periodStart: string;
  periodEnd: string;
  scopeId: string | null;
  metrics: Record<string, number>;
  computedAt: string;
}

export interface SnapshotRunResult {
  period: AnalyticsPeriod;
  periodStart: string;
  periodEnd: string;
  snapshotsWritten: number;
}

export interface SystemHealth {
  apiServer: string;
  database: string;
  redisCache: string;
  workerQueue: string;
}

export interface LiveActivity {
  id: string;
  title: string;
  desc: string;
  time: string;
  type: string;
}

export interface PlatformProductBreakdownItem {
  productId: string;
  title: string;
  imageUrl: string | null;
  sellerName: string;
  qty: number;
  revenuePaise: number;
  orderCount: number;
}

export interface PlatformProductBreakdownResponse {
  totalRevenuePaise: number;
  todayRevenuePaise: number;
  orderCount: number;
  products: PlatformProductBreakdownItem[];
}

export function analyticsEndpoints(http: AxiosInstance) {
  return {
    dashboard: (params?: { period?: AnalyticsPeriod }) =>
      http.get<Dashboard>('/admin/analytics/dashboard', { params }).then((r) => r.data),
    platformProductBreakdown: (range?: 'today' | '7d' | '30d' | 'all') =>
      http
        .get<PlatformProductBreakdownResponse>('/admin/analytics/product-breakdown', {
          params: range ? { range } : undefined,
        })
        .then((r) => r.data),
    listSnapshots: (params?: {
      domain?: string;
      period?: string;
      scopeId?: string;
      from?: string;
      limit?: number;
    }) =>
      http
        .get<{ data: AnalyticsSnapshot[] }>('/admin/analytics/snapshots', { params })
        .then((r) => r.data.data),
    getCustomer: (userId: string) =>
      http.get<CustomerMetrics>(`/admin/analytics/customers/${userId}`).then((r) => r.data),
    getVendor: (vendorId: string) =>
      http.get<VendorMetrics>(`/admin/analytics/vendors/${vendorId}`).then((r) => r.data),
    getFinance: () => http.get<FinanceMetrics>('/admin/analytics/finance').then((r) => r.data),
    getOps: () => http.get<OpsMetrics>('/admin/analytics/ops').then((r) => r.data),
    getPlatform: () => http.get<PlatformMetrics>('/admin/analytics/platform').then((r) => r.data),
    runSnapshot: (body: { period?: AnalyticsPeriod; asOf?: string }) =>
      http.post<SnapshotRunResult>('/admin/analytics/snapshot/run', body).then((r) => r.data),
    systemHealth: () =>
      http.get<SystemHealth>('/admin/analytics/system-health').then((r) => r.data),
    liveActivity: () =>
      http.get<LiveActivity[]>('/admin/analytics/live-activity').then((r) => r.data),

    // Self-service
    myVendor: () => http.get<VendorMetrics>('/analytics/vendor/me').then((r) => r.data),
    myCustomer: () => http.get<CustomerMetrics>('/analytics/customer/me').then((r) => r.data),
    myVendorBreakdown: (range?: '7d' | '30d' | '90d' | 'all') =>
      http
        .get<{
          totalRevenuePaise: number;
          orderCount: number;
          uniqueCustomers: number;
          repeatCustomers: number;
          topProducts: Array<{
            productId: string;
            title: string;
            qty: number;
            revenue: number;
            orders: number;
          }>;
          topCategories: Array<{
            categoryId: string;
            name: string;
            revenue: number;
            orders: number;
          }>;
          topCustomers: Array<{
            customerId: string;
            name: string;
            email: string;
            orders: number;
            revenue: number;
            lastOrder: string;
          }>;
          dailySeries: Array<{ date: string; revenue: number; orders: number }>;
        }>('/analytics/vendor/me/breakdown', { params: range ? { range } : undefined })
        .then((r) => r.data),
  };
}

export type AnalyticsEndpoints = ReturnType<typeof analyticsEndpoints>;
